import { DecimalPipe, NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { APPOINTMENT_CONSTANT } from '../../../../constants/appointment-constants';
import { TRANSLATION_TEMPLATES } from '../../../../constants/translation-templates-constants';
import { PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS, PARENT_TO_CHILD_IFRAME_EVENTS, PARENT_TO_CHILD_IFRAME_VALIDATION_CONTROL_NAME, WIDGET_CONSTANTS } from '../../../../constants/widget-constants';
import { AppointmentServiceCategoryDao, AppointmentServiceDao } from '../../../../db-models/appointment-service-dao';
import { StoresDao } from '../../../../db-models/stores-dao';
import { UserDao } from '../../../../db-models/user-data-dao';
import { CustomBookingMessageTemplate } from '../../../../db-models/widget-conf-dao';
import { GlobalObjects, Partner } from '../../../../models/global';
import { AppointmentState, ConferenceState } from '../../../../models/state.model';
import { WidgetColorConf } from '../../../../models/widget-color.model';
import { CustomEventService } from '../../../../services/custom-event.service';
import { FormsService } from '../../../../services/forms.service';
import { HelperService } from '../../../../services/helper.service';
import { LoggerService } from '../../../../services/logger.service';
import { PartnerService } from '../../../../services/partner.service';
import { isPlainPostcode } from '../../../../services/store-postcode.util';
import { WidgetUtilService } from '../../../../services/widget-util.service';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { NewAlertComponent } from '../../../../shared/components/new-alert/new-alert.component';
import { AddressFormatPipe } from '../../../../shared/pipes/address-format.pipe';
import { TranslationPipe } from '../../../../shared/pipes/translation.pipe';
import { TrustHtmlPipe } from '../../../../shared/pipes/trust-html.pipe';
import { CwCardComponent } from '../../../common/theme/cw-card/cw-card.component';
import { CwRadioTickButtonComponent } from '../../../common/theme/cw-radio-tick-button/cw-radio-tick-button.component';
import { CwTitleComponent } from '../../../common/theme/cw-title/cw-title.component';
import { AppointmentServicesComponent } from '../appointment-services/appointment-services.component';

const pipes = [DecimalPipe, TranslateModule, TranslationPipe, TrustHtmlPipe, AddressFormatPipe];

@Component({
  selector: 'app-appointment-list',
  templateUrl: './appointment-list.component.html',
  styleUrls: ['./appointment-list.component.scss'],
  imports: [CwTitleComponent, NgSelectModule, FormsModule, ButtonComponent, CwCardComponent, NgClass, CwRadioTickButtonComponent, AppointmentServicesComponent, NewAlertComponent, ...pipes]
})

export class AppointmentListComponent implements OnInit, OnChanges, AfterViewInit {

  private translate = inject(TranslateService);
  private widgetUtilService = inject(WidgetUtilService);
  private formsService = inject(FormsService);
  private customEventService = inject(CustomEventService);
  private helperService = inject(HelperService);
  private partnerService = inject(PartnerService);
  private destroy = inject(DestroyRef);

  protected isWhitelabel(): boolean {
    return this.partnerService.hasFeature('is_whitelabel_enabled');
  }

  @Input() globals: GlobalObjects;
  @Input() stores: StoresDao[] = [];
  @Input() hideStoreSelection = false;
  @Input() appointmentState: AppointmentState;
  @Input() widgetConf: any;
  @Input() selectedStore: StoresDao;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() appointmentServicesByCat: {
    category: string,
    _translations: any,
    is_multi_language: number,
    appointmentServiceCategory: AppointmentServiceCategoryDao,
    aServicesOfCat: AppointmentServiceDao[],
    position: number
  }[];
  @Input() partner: Partner;
  @Input() noWorkers: boolean;
  @Input() lang: string;
  @Input() widgetColorConf: WidgetColorConf;
  @Input() previousButtonTemplate: CustomBookingMessageTemplate;
  @Input() nextButtonTemplate: CustomBookingMessageTemplate;
  @Input() selectedStoreZipCode: number;
  @Input() subscriptionTypeId: number;
  @Input() conferenceState: ConferenceState;
  @Input() isServicesLoading = false;
  // True once a services load has actually completed. Distinguishes "loaded and empty" from
  // "not requested yet" — without it the empty-state message flashes before the first load.
  @Input() servicesResolved = false;
  // The services request failed rather than returning an empty list, so the visitor is told to
  // retry instead of being told the provider cannot be booked.
  @Input() servicesLoadFailed = false;
  // True once the stores lookup has come back, either way. Distinguishes "no stores exist" from
  // "the stores request has not returned yet".
  @Input() storesResolved = false;
  @Input() enteredAppointmentFromTour = false;

  @Output() navigateToEvent = new EventEmitter<any>();
  @Output() storeSelectedEvent = new EventEmitter<number>();
  @Output() resetFormEvent = new EventEmitter<any>();
  @Output() serviceChangeEvent = new EventEmitter<{ event: Event, service: AppointmentServiceDao }>();
  @Output() disabledServiceTabEvent = new EventEmitter<any>();
  @Output() newStoresEvent = new EventEmitter<StoresDao[]>();
  @Output() backToTourEvent = new EventEmitter<{ resetSelection: boolean }>();

  protected readonly appointmentConstant = APPOINTMENT_CONSTANT;
  protected readonly widgetConstant = WIDGET_CONSTANTS;
  protected readonly environment = environment;
  protected readonly parentToChildIframeEvents = PARENT_TO_CHILD_IFRAME_EVENTS;
  protected readonly parentToChildIframeValidationControlName = PARENT_TO_CHILD_IFRAME_VALIDATION_CONTROL_NAME;
  protected readonly templateContent = TRANSLATION_TEMPLATES;

  protected activePanels = ['static-0'];
  protected cartSupported: boolean;
  protected isMobile: boolean;
  protected isStoreLoading = false;
  protected storeLabelTemplate: CustomBookingMessageTemplate;
  protected widgetStoreSelectionHint: CustomBookingMessageTemplate;
  protected stepErrorTemplate: CustomBookingMessageTemplate;
  protected readonly showStepValidationError = signal(false);


  /** Postcode / zip search box — the only selector that does not need a second store. */
  protected get storeSelectorIsPostcodeSearch(): boolean {
    return ((this.partner?.enable_store_postcode_feature === 1 && this.widgetConf?.show_stores_as_dropdown === 1) ||
      this.partner?.automatic_store_zip_search === 1) &&
      ((this.globals?.isInternal && this.partner?.internal_widget_store_search_list === 1) || !this.globals?.isInternal);
  }

  /** Radio-card list of stores. */
  protected get storeSelectorIsCardList(): boolean {
    return this.stores?.length > 1 &&
      this.partner?.enable_store_postcode_feature !== 1 &&
      this.widgetConf?.show_stores_as_dropdown !== 1;
  }

  /** Plain dropdown of stores. */
  protected get storeSelectorIsDropdown(): boolean {
    return (this.stores?.length > 1 &&
      (this.globals?.isInternal && this.partner?.internal_widget_store_search_list === 1)) ||
      (this.stores?.length > 1 &&
        !this.globals?.isInternal &&
        this.partner?.enable_store_postcode_feature !== 1 &&
        this.partner?.automatic_store_zip_search !== 1 &&
        this.widgetConf?.show_stores_as_dropdown === 1);
  }

  /**
   * Whether a store selector is actually on screen — i.e. the visitor has a way forward.
   *
   * The three getters above ARE the template's `@if/@else if/@else if` conditions; the template
   * uses these same getters, so this can never drift out of sync with what actually renders.
   * `!hideStoreSelection` alone is not enough: the chain has no final `@else`, so several partner
   * shapes leave the region empty while `hideStoreSelection` is false.
   */
  protected get storeSelectorVisible(): boolean {
    return !this.hideStoreSelection &&
      (this.storeSelectorIsPostcodeSearch || this.storeSelectorIsCardList || this.storeSelectorIsDropdown);
  }

  /**
   * A store is selected, its services have loaded, and there are none to offer. The next button
   * is pointless in that state — and with widget_buttons_always_enabled it is even clickable,
   * producing a "please select a service" error for a list that cannot be populated.
   */
  protected get hasNoBookableServices(): boolean {
    return !!this.appointmentState?.store && this.servicesResolved && !this.appointmentServicesByCat?.length;
  }

  /**
   * The lookup came back with no store at all, so there is nothing to select and nothing to book.
   *
   * Previously this state threw before it could render (the store-resolution log dereferenced an
   * empty array); with that fixed it would otherwise be a silent blank step — no selector, because
   * none of the selector branches match, and no services message, because that one requires a
   * selected store.
   *
   * `!storeSelectorVisible` is load-bearing, not belt-and-braces: the postcode search box is the
   * one selector that does NOT require a store to exist, so with a zip-search partner the visitor
   * still has a way forward and must not be told booking is unavailable.
   */
  protected get hasNoStoresAtAll(): boolean {
    return this.storesResolved && !this.isStoreLoading && !this.stores?.length &&
      !this.appointmentState?.store && !this.storeSelectorVisible;
  }

  private leadGeneratorPostcode: string;
  private postcodeText: string;
  protected searchStores = new EventEmitter<string>();
  private parentToChildIframeEventActions = PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS;
  protected showConferenceError = false;

  ngOnInit(): void {
    this.translate.onLangChange.subscribe(lang => this.lang = lang.lang);

    this.customEventService.iFrameNavigationFromParent
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(action => {
        switch (action) {
          case this.parentToChildIframeEventActions.PREVIOUS:
            if (this.partner?.is_customer_profiles_feature_enabled === 1 && this.partner?.customer_profiles?.length > 1) {
              this.navigateTo(this.appointmentConstant.CUSTOMER_TYPE);
            } else if (this.partner?.is_customer_profiles_feature_enabled !== 1) {
              this.widgetUtilService.sendMessageToParent({
                eventName: this.parentToChildIframeEvents.VALIDATION,
                controls: [
                  this.parentToChildIframeValidationControlName.CUSTOMER_PROFILE_DISABLED
                ]
              });
            }
            break;
          case this.parentToChildIframeEventActions.NEXT:
            if (this.appointmentState.services?.length) {
              this.navigateTo(this.appointmentConstant.WORKER, false);
            } else if (this.appointmentState.store === 0 || this.appointmentState.store === null) {
              this.widgetUtilService.sendMessageToParent({
                eventName: this.parentToChildIframeEvents.VALIDATION,
                controls: [
                  this.parentToChildIframeValidationControlName.STORE,
                  this.parentToChildIframeValidationControlName.SERVICE
                ]
              });
            } else if (this.appointmentState.services?.length === 0 || this.appointmentState.store === null) {
              this.widgetUtilService.sendMessageToParent({
                eventName: this.parentToChildIframeEvents.VALIDATION,
                controls: [
                  this.parentToChildIframeValidationControlName.SERVICE
                ]
              });
            }
            break;
        }
      });

    this.customEventService.redefineDefaultWorkerIdEvent
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(workers => this.redefineDefaultWorkerId(workers))

    this.customEventService.servicesLoadedEvent
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(() => {
        this.showConferenceError = (this.conferenceState.uuid && this.appointmentServicesByCat?.length === 0);
      });

    let tempPostCode: string;
    this.searchStores.pipe(
      distinctUntilChanged(),
      debounceTime(300),
      filter((term: string) => term !== null),
      switchMap((term: string) => {
        this.isStoreLoading = true;
        tempPostCode = term;
        if (term && term.trim() !== '' && String(term).length >= 4) {
          this.leadGeneratorPostcode = term;
          this.postcodeText = term;
          this.stores = [];
          let enableStoreSorting = true;

          // Check if PLZ/ZIP search is enable then don't sort
          if (
            this.partner?.enable_store_postcode_feature === 1 ||
            this.partner?.automatic_store_zip_search === 1
          ) {
            enableStoreSorting = false;
          }
          return this.formsService.getStoresByPostCode(term, enableStoreSorting);
        } else {
          this.stores = [];
          return of([]);
        }
      })
    ).subscribe({
      next: stores => {
        this.stores = stores;
        this.newStoresEvent.emit(stores);
        if (this.stores?.length === 1) {
          this.onStoreSelected(this.stores[0]);
        } else if (this.stores?.length > 1) {
          const normalizeZip = (z: string | number) => String(z ?? '').trim().toUpperCase().replace(/\s+/g, '');
          const filteredStores = this.stores?.filter(store => normalizeZip(tempPostCode) === normalizeZip(store.zip));
          if (filteredStores.length === 1) {
            this.onStoreSelected(filteredStores[0]);
          } else if (this.appointmentState.store) {
            this.preSelectStore();
          }
        } else if (this.appointmentState.store) {
          this.preSelectStore();
        }
        this.isStoreLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        LoggerService.error(error);
        this.isStoreLoading = false;
      }
    });

    if (
      (this.partner.enable_store_postcode_feature === 1 || this.partner.automatic_store_zip_search === 1)
      && !this.appointmentState.store
      && (
        (this.globals?.isInternal && this.partner?.internal_widget_store_search_list === 1)
        ||
        (!this.globals?.isInternal)
      )
    ) {
      this.stores = [];
      if (this.selectedStoreZipCode) {
        this.searchStores.emit(this.selectedStoreZipCode + '');
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.appointmentState?.leadGeneratorPostcode) {
      this.searchStores.emit(this.appointmentState.leadGeneratorPostcode);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.getSelectedStore(this.appointmentState.store);

    if (changes?.widgetTemplates?.currentValue) {
      this.widgetTemplates?.length && this.setupTemplates();
    }
  }

  private preSelectStore(): void {
    if (!this.appointmentState.selectedStorePostcodeMappingId) {
      const preSelectedStore = this.stores.find(store => store.id === this.appointmentState.store);
      preSelectedStore && this.onStoreSelected(preSelectedStore);
    }
  }

  protected onStoreSelected(store: StoresDao): void {
    if (!store?.id) {
      // A cleared store search must not leave its postcode behind as a prefill.
      this.widgetUtilService.setStoreSearchZipCode('');
      return;
    }

    // When single store is mapped with multiple postcodes or cities when user navigates back
    // it preselects wrong store (technically store id is valid but city name is wrong) so keeping
    // post code mapping id for valid reference (Fix for EGK)
    // appointmentState.store
    this.appointmentState.selectedStorePostcodeMappingId = store?.postcode_sore_id ?? `${store.id}`;

    const storeSearchTerm = (this.postcodeText ?? '').trim();
    this.widgetUtilService.setStoreSearchZipCode(isPlainPostcode(storeSearchTerm) ? storeSearchTerm : '');
    this.appointmentState.store = store.id;
    this.setDefaultWorkerState(store.default_worker_id, store.default_worker);
    this.appointmentState.leadGeneratorPostcode = this.leadGeneratorPostcode;
    this.storeSelectedEvent.emit(store.id);
  }

  private redefineDefaultWorkerId(workers: UserDao[]) {
    const selectedStore = this.stores.find(store => store.id === this.appointmentState.store);
    if (selectedStore?.default_worker_id?.length) {

      const hasSupportedWorkers = workers.find(worker => worker.id === selectedStore.default_worker_id[0]);
      if (hasSupportedWorkers) {
        this.setDefaultWorkerState(selectedStore.default_worker_id, selectedStore.default_worker);
      } else {
        this.setDefaultWorkerState();
      }
    }
  }

  private setDefaultWorkerState(workerIds: number[] = [], workers: UserDao[] = []) {
    const hasOneWorker = workerIds?.length === 1;
    this.appointmentState.storeDefaultWorkerIds = workerIds ?? [];
    this.appointmentState.default_worker_id = hasOneWorker ? workerIds[0] : null;
    this.appointmentState.default_worker = hasOneWorker ? workers?.[0] : null;
  }

  protected resetForm(): void {
    this.resetFormEvent.emit();
  }

  protected navigateTo(page: string, isFirstTime?: boolean): void {
    // In case of conference or combi service skip worker selection and move to date step
    if (
      page === this.appointmentConstant.WORKER &&
      (this.conferenceState?.uuid || this.appointmentState.isCombiServiceSelected)
    ) {
      page = this.appointmentConstant.DATE;
    }

    this.navigateToEvent.next({ page: page, isFirstTime: isFirstTime });
  }

  protected shortcutNavigateTo(): void {
    this.navigateTo(this.appointmentConstant.WORKER, false);
  }

  protected onNextButtonClick(): void {
    if (Number(this.partner?.widget_buttons_always_enabled) === 1 && this.appointmentState.services.length < 1) {
      this.showStepValidationError.set(true);
      return;
    }
    this.showStepValidationError.set(false);
    this.navigateTo(this.appointmentConstant.WORKER, false);
  }

  private getSelectedStore(storeId: number): StoresDao {
    if (storeId) {
      const founded = this.stores.find(store => store.id === storeId);
      if (founded) {
        this.selectedStore = founded;
        return founded;
      } else {
        return undefined;
      }
    }
    return undefined;
  }

  private setupTemplates(): void {
    this.storeLabelTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_APPOINTMENT_STORE_LABEL
    );
    this.widgetStoreSelectionHint = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_STORE_SELECTION_HINT
    );
    this.stepErrorTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_STEP_ERROR_SERVICE
    );
  }
}
